import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { CircleUser, Crosshair, ShoppingCart, Store } from "lucide-react";
import { EASE } from "../motion";
import { StoreBadges } from "../StoreBadges";
import { CampaignScreen } from "./CampaignScreen";
import { AIScreen } from "./AIScreen";
import { APP_SCREENS } from "@/lib/site";

const DURATION = 5000;
const CODED = { campagna: CampaignScreen, ia: AIScreen };
const NAV_ACTIVE = { campagna: "campagna", ia: "studio" };
const NAV = [
  { id: "campagna", icon: Crosshair, l: "Campagna" },
  { id: "studio", icon: Store, l: "Studio" },
  { id: "shop", icon: ShoppingCart, l: "Shop" },
  { id: "profilo", icon: CircleUser, l: "Profilo" },
];

const StatusBar = () => (
  <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-6 pt-3 text-[9.5px] font-bold text-white">
    <span>22:52</span>
    <span className="flex items-center gap-1"><i className="fa-solid fa-signal text-[8px]" /><i className="fa-solid fa-battery-three-quarters text-[9px]" /></span>
  </div>
);

const TabBar = ({ active }) => (
  <div className="absolute inset-x-0 bottom-0 z-10 border-t border-[#222226] bg-[#0F0F12] px-2 pb-2 pt-2">
    <div className="flex justify-around">
      {NAV.map(({ id, icon: Icon, l }) => (
        <span key={id} className={`flex flex-col items-center gap-1 ${id === active ? "text-[#E03A3A]" : "text-[#8A8A90]"}`}>
          <Icon className="h-4 w-4" />
          <span className="text-[7px] font-extrabold uppercase tracking-wider">{l}</span>
        </span>
      ))}
    </div>
    <span className="mx-auto mt-2 block h-[3px] w-20 rounded-full bg-white/90" />
  </div>
);

const Screen = ({ screen }) => {
  if (screen.image) return <img src={screen.image} alt={`InkMaster · ${screen.label}`} className="h-full w-full object-cover object-top" data-testid={`app-screen-${screen.id}`} />;
  const Coded = CODED[screen.id];
  return (
    <>
      <StatusBar />
      <Coded />
      <TabBar active={NAV_ACTIVE[screen.id]} />
    </>
  );
};

const Phone = ({ screen }) => (
  <div className="relative h-[625px] w-[292px] rounded-[46px] border-[9px] border-[#1d1d20] bg-ink shadow-[0_60px_120px_-20px_rgba(0,0,0,0.9)]">
    <div className="absolute inset-0 overflow-hidden rounded-[37px]">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={screen.id}
          className="absolute inset-0"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <Screen screen={screen} />
        </motion.div>
      </AnimatePresence>
    </div>
    <div className="absolute left-1/2 top-2 z-20 h-5 w-20 -translate-x-1/2 rounded-full bg-[#1d1d20]" />
  </div>
);

const Tabs = ({ active, onSelect, playing, delay }) => (
  <div className="flex w-full max-w-[460px] border border-line" role="tablist" data-testid="app-screen-tabs">
    {APP_SCREENS.map((s, i) => (
      <button
        key={s.id}
        type="button"
        role="tab"
        aria-selected={i === active}
        onClick={() => onSelect(i)}
        data-testid={`app-screen-tab-${s.id}`}
        className={`relative flex-auto overflow-hidden whitespace-nowrap border-r border-line px-1.5 py-3 font-mono text-[9px] uppercase tracking-[0.1em] sm:px-2 sm:text-[10px] sm:tracking-[0.18em] transition-colors duration-300 last:border-r-0 ${i === active ? "text-bone" : "text-smoke hover:text-bone"}`}
      >
        {s.label}
        {i === active && (
          <motion.span
            key={`bar-${active}-${delay}`}
            className="absolute inset-x-0 bottom-0 h-[2px] origin-left bg-acid"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: playing ? 1 : 0 }}
            transition={{ duration: playing ? delay / 1000 : 0, ease: "linear" }}
          />
        )}
      </button>
    ))}
  </div>
);

export const PhoneShowcase = ({ tilt }) => {
  const [active, setActive] = useState(0);
  const [delay, setDelay] = useState(DURATION);
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-20% 0px" });

  useEffect(() => {
    if (!inView) return undefined;
    const t = setTimeout(() => {
      setDelay(DURATION);
      setActive((a) => (a + 1) % APP_SCREENS.length);
    }, delay);
    return () => clearTimeout(t);
  }, [active, inView, delay]);

  const select = (i) => {
    setDelay(DURATION * 2);
    setActive(i);
  };

  useEffect(() => {
    APP_SCREENS.forEach((s) => {
      if (s.image) new Image().src = s.image;
    });
  }, []);

  const screen = APP_SCREENS[active];
  return (
    <div ref={ref} className="flex flex-col items-center">
      <div style={{ perspective: 1400 }}>
        <motion.div style={tilt} data-testid="app-phone-mockup">
          <Phone screen={screen} />
        </motion.div>
      </div>
      <div className="relative z-10 mt-14 flex w-full flex-col items-center">
        <Tabs active={active} onSelect={select} playing={inView} delay={delay} />
        <AnimatePresence mode="wait">
          <motion.p
            key={screen.id}
            data-testid="app-screen-caption"
            className="mt-4 min-h-[4.5rem] max-w-[400px] text-center text-sm leading-relaxed text-bone/70"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
          >
            {screen.caption}
          </motion.p>
        </AnimatePresence>
        <div className="mt-4 w-full" data-testid="app-store-badges">
          <StoreBadges appStoreTestId="app-store-badge" googlePlayTestId="google-play-badge" className="justify-center" />
        </div>
      </div>
    </div>
  );
};
